import api from "./mainApi";
import { beginColorSync, commitColorSyncPage, finishColorSync } from "../localDB/colorDb";

let inFlight;
let activeOptions;

async function run(onPage, onComplete, options) {
  let pending = await beginColorSync(options);
  // An explicit startup refresh may join while the daily checkpoint is being read.
  if (!pending && !options.dueOnly) pending = await beginColorSync();
  let result;
  while (pending) {
    console.log("[ColorSync] Sync started/resumed", {
      full: pending.full, savedPages: pending.pages, afterId: pending.afterId,
      upperId: pending.upperId, since: pending.since
    });
    while (!pending.done) {
      console.log("[ColorSync] Requesting page", { afterId: pending.afterId, size: 10 });
      const { data } = await api.get("/colors/sync", {
        rejectOnCancel: true,
        timeout: 30000,
        params: {
          afterId: pending.afterId,
          upperId: pending.upperId ?? undefined,
          since: pending.since ?? undefined,
          size: 10
        }
      });
      console.log("[ColorSync] Page received", {
        count: data?.content?.length, nextAfterId: data?.nextAfterId,
        upperId: data?.upperId, last: data?.last
      });
      // Reject malformed responses rather than committing an unusable checkpoint.
      if (!data || !Array.isArray(data.content) || typeof data.last !== "boolean" ||
          !Number.isSafeInteger(data.nextAfterId) || !Number.isSafeInteger(data.upperId) ||
          data.upperId < pending.afterId || data.nextAfterId < pending.afterId ||
          data.nextAfterId > data.upperId || typeof data.startedAt !== "string" || !data.startedAt ||
          (pending.upperId != null && data.upperId !== pending.upperId) ||
          (!data.last && data.nextAfterId <= pending.afterId)) {
        throw new Error("Invalid color sync response");
      }
      pending = await commitColorSyncPage(pending, data);
      console.log("[ColorSync] Page saved", {
        pages: pending.pages, afterId: pending.afterId, count: data.content.length
      });
      onPage(data.content);
    }
    result = await finishColorSync(pending);
    onComplete(result);
    console.log("[ColorSync] Complete", {
      count: result.colors.length, lastFullRefresh: result.lastFullRefresh,
      lastQueryDate: result.lastQueryDate
    });
    // Finish a saved run first, then catch up if its full refresh is from an older day.
    pending = await beginColorSync({ dueOnly: true });
  }
  return result;
}

// Do not wait forever behind a suspended browser tab holding the sync lock.
async function withColorSyncLock(work) {
  if (!navigator.locks) return work();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    return await navigator.locks.request("colors-sync", { signal: controller.signal }, () => {
      clearTimeout(timer);
      return work();
    });
  } finally {
    clearTimeout(timer);
  }
}

export function refreshColors(onPage = () => {}, onComplete = () => {}, options = {}) {
  if (!inFlight) {
    activeOptions = { ...options };
    const work = () => run(onPage, onComplete, activeOptions);
    inFlight = Promise.resolve().then(() => withColorSyncLock(work)).finally(() => {
      inFlight = null;
      activeOptions = null;
    });
  } else {
    // An explicit refresh must take priority over an overlapping daily-only check.
    if (!options.dueOnly) activeOptions.dueOnly = false;
  }
  return inFlight;
}
